import {
    AuthContext,
    CategoryTags,
    ChatPanel,
    ComposePanel,
    CoreFetch,
    FilterMethod,
    IComment,
    ICustomFilter,
    IMessage,
    ITableColumn,
    ITableFetchOptions,
    ListProviderMessagesOrderEnum,
    MessageApi,
    MessageLinkEntityType,
    MessageList,
    MessagingProvider,
    ProviderClaim,
    ProviderMessage,
    ProviderMessageSortType,
    ToastContext,
} from '@enter/core';
import { DataTableCard, PageLayout, PageLayoutHeader, PageLayoutRow, PageLayoutTitle, useDocumentView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageDetail, IMessageListingRequest } from 'stores';
import * as S from './messaging.styles';

interface IMatchParams {
    messageId: string;
}

type IMessagingProps = IInjectedRootStore;

export const Messaging: React.FunctionComponent<IMessagingProps> = inject('rootStore')(
    observer((props: IMessagingProps) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        document.title = 'ENTER Platform - Messaging';
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        const authContext = useContext(AuthContext);
        const toastContext = useContext(ToastContext);
        const dialogContext = useContext(DialogContext);
        const [claims, setClaims] = useState<ProviderClaim[] | undefined>();
        const [isDetailLoading, setDetailLoading] = useState<boolean>();
        const [isListLoading, setListLoading] = useState<boolean>();
        const [areCategoriesLoaded, setAreCategoriesLoaded] = useState<boolean>(false);
        const [areUsersLoaded, setAreUsersLoaded] = useState<boolean>(false);
        const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [messageId, setMessageId] = useState<string | undefined>();
        const [messageDetail, setMessageDetail] = useState<IMessageDetail | undefined>();
        // Block list fetch if there's a messageId - we need to load message detail first
        const [allowListFetch, setAllowListFetch] = useState<boolean>(!params.messageId || params.messageId === 'compose');
        const [openFile] = useDocumentView();
        const isLoadingRef = useRef<string | null>(null);
        const isCancelledRef = useRef<boolean>(false);
        const navigatedAwayFromErrorRef = useRef<boolean>(false);
        const failedMessageIdsRef = useRef<Set<string>>(new Set());
        const messageChangedRef = React.useRef<boolean>(false);

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = false;

        // Force MobX to track these observables by accessing them at component level
        const messageStore = rootStore.messageStore;
        const storeInboxMessages = messageStore.inboxMessages;
        const storeInboxTotal = messageStore.inboxTotal;
        const storeDoneMessages = messageStore.doneMessages;
        const storeDoneTotal = messageStore.doneTotal;
        const storeUnreadInboxTotal = messageStore.unreadInboxTotal;


        // Load message categories and assignees (provider users) before rendering data
        useEffect(() => {
            let isCancelled = false;

            const loadMeta = async () => {
                try {
                    await Promise.all([
                        rootStore.messageStore.loadMessageCategories(),
                        rootStore.messageStore.loadProviderUsers(),
                    ]);
                } finally {
                    if (!isCancelled) {
                        setAreCategoriesLoaded(true);
                        setAreUsersLoaded(true);
                    }
                }
            };

            loadMeta();

            return () => {
                isCancelled = true;
            };
        }, [rootStore.messageStore, authContext]);

        useEffect(() => {
            const paramsMessageId = params.messageId;
            isCancelledRef.current = false;
            
            const loadMessage = async () => {
                if (paramsMessageId && paramsMessageId !== 'compose') {
                    if (failedMessageIdsRef.current.has(paramsMessageId)) {
                        isCancelledRef.current = true;
                        navigatedAwayFromErrorRef.current = true;
                        isLoadingRef.current = null;
                        setMessageDetail(undefined);
                        setMessageId(undefined);
                        setAllowListFetch(false);
                        navigate('..', { replace: true, relative: 'path' });
                        return;
                    }
                    
                    if (isLoadingRef.current === paramsMessageId) {
                        return;
                    }
                    
                    isLoadingRef.current = paramsMessageId;
                    navigatedAwayFromErrorRef.current = false;
                    setAllowListFetch(false);
                    setMessageId(paramsMessageId);
                    setMessageDetail(undefined);
                    setDetailLoading(true);
                    try {
                        const detail = await rootStore.messageStore.loadMessageDetail(paramsMessageId, true);
                        
                        if (isCancelledRef.current || isLoadingRef.current !== paramsMessageId) {
                            return;
                        }
                        
                        if (detail) {
                            rootStore.messageStore.markAsRead(paramsMessageId);
                            setMessageDetail(detail);
                            failedMessageIdsRef.current.delete(paramsMessageId);
                            navigatedAwayFromErrorRef.current = false;
                            setAllowListFetch(true);
                        } else {
                            failedMessageIdsRef.current.add(paramsMessageId);
                            setMessageDetail(undefined);
                            setMessageId(undefined);
                            setAllowListFetch(false);
                            isLoadingRef.current = null;
                            navigate('..', { replace: true, relative: 'path' });
                        }
                    } catch (err: any) {
                        if (isCancelledRef.current || isLoadingRef.current !== paramsMessageId) {
                            return;
                        }
                        
                        const isAbortError = 
                            (err instanceof Error && err.name === 'AbortError') ||
                            (err instanceof Error && err.message?.includes('aborted')) ||
                            (typeof err === 'object' && err !== null && 'name' in err && err.name === 'AbortError');
                        
                        if (isAbortError) {
                            setAllowListFetch(false);
                        } else {
                            failedMessageIdsRef.current.add(paramsMessageId);
                            isCancelledRef.current = true;
                            navigatedAwayFromErrorRef.current = true;
                            isLoadingRef.current = null;
                            setMessageDetail(undefined);
                            setMessageId(undefined);
                            setAllowListFetch(false);
                            navigate('..', { replace: true, relative: 'path' });
                        }
                    } finally {
                        if (!isCancelledRef.current && isLoadingRef.current === paramsMessageId) {
                            setDetailLoading(false);
                            isLoadingRef.current = null;
                        }
                    }
                } else {
                    if (!isCancelledRef.current && !navigatedAwayFromErrorRef.current) {
                        isLoadingRef.current = null;
                        setAllowListFetch(true);
                        setMessageId(undefined);
                        setMessageDetail(undefined);
                        setDetailLoading(false);
                    } else if (navigatedAwayFromErrorRef.current) {
                        navigatedAwayFromErrorRef.current = false;
                        isLoadingRef.current = null;
                        setMessageId(undefined);
                        setMessageDetail(undefined);
                        setDetailLoading(false);
                        setAllowListFetch(false);
                    }
                }
            };
            
            loadMessage();
            
            return () => {
                isCancelledRef.current = true;
            };
        }, [params.messageId, rootStore.messageStore, authContext, navigate]);

        useEffect(() => {
            if (allowListFetch && !isDetailLoading && fetchOptions) {
                handleFetchList(fetchOptions);
            }
        }, [allowListFetch, isDetailLoading]);

        const fetchMessageListing = async (data: ITableFetchOptions): Promise<void> => {
            const { pageIndex, pageSize, sortBy, validFilters } = data;
            const sort: ProviderMessageSortType[] = [];
            const order: ListProviderMessagesOrderEnum[] = [];
            _.forEach(sortBy, rule => {
                let s;
                switch (rule.id) {
                    case 'Subject':
                        s = ProviderMessageSortType.SUBJECT;
                        break;
                    case 'Created':
                        s = ProviderMessageSortType.CREATED;
                        break;
                    case 'Updated':
                        s = ProviderMessageSortType.UPDATED;
                        break;
                    case 'Purpose':
                        s = ProviderMessageSortType.PURPOSE;
                        break;
                    default:
                        s = undefined;
                }
                if (s) {
                    sort.push(s);
                    order.push(
                        rule.desc ? ListProviderMessagesOrderEnum.DESCENDING : ListProviderMessagesOrderEnum.ASCENDING,
                    );
                }
            });
            // Remove any existing filterOpen filters to avoid duplicates
            const filtersWithoutOpen = validFilters.filter(f => f.filterId !== 'filterOpen');
            
            // Fetch both inbox and done messages in parallel
            // This ensures we always have the correct data regardless of which tab is active
            await Promise.all([
                // Fetch inbox messages (filterOpen=EQ:1)
                rootStore.messageStore.loadMessageListing({
                    page: pageIndex,
                    pageSize,
                    sort,
                    order,
                    filters: [
                        ...filtersWithoutOpen,
                        {
                            filterId: 'filterOpen',
                            filterMethod: FilterMethod.EQ,
                            filterType: 'String',
                            value: '1', // Inbox messages
                        },
                    ],
                }),
                // Fetch done messages (filterOpen=EQ:0)
                rootStore.messageStore.loadMessageListing({
                    page: pageIndex,
                    pageSize,
                    sort,
                    order,
                    filters: [
                        ...filtersWithoutOpen,
                        {
                            filterId: 'filterOpen',
                            filterMethod: FilterMethod.EQ,
                            filterType: 'String',
                            value: '0', // Done messages
                        },
                    ],
                }),
            ]);
        };

        const refresh = async (): Promise<void> => {
            if (fetchOptions) {
                handleFetchList(fetchOptions);
            }
        };

        const handleFetchList = async (newFetchOptions: ITableFetchOptions): Promise<void> => {
            setFetchOptions(newFetchOptions);
            
            if (!allowListFetch || isDetailLoading) {
                return;
            }
            
            try {
                setListLoading(true);
                
                await fetchMessageListing(newFetchOptions);
                setListLoading(false);
            } catch (err) {
                const isAbortError = 
                    (err instanceof Error && err.name === 'AbortError') ||
                    (err instanceof Error && err.message?.includes('aborted')) ||
                    (typeof err === 'object' && err !== null && 'name' in err && err.name === 'AbortError');
                
                if (!isAbortError) {
                    dialogContext.showError(err);
                }
                setListLoading(false);
            }
        };

        const refreshMessageDetail = async (mid: string, forceFetch?: boolean): Promise<void> => {
            if (mid === 'compose') {
                return;
            }

            const detail = await rootStore.messageStore.loadMessageDetail(mid, forceFetch);
            if (detail && !_.isEqual(detail, messageDetail)) {
                setMessageDetail(detail);
                setMessageId(mid);
                setDetailLoading(false);
            }
        };

        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            setClaims([]);
            const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
            setClaims(results);
            return results;
            // return new Promise((resolve, reject) => {
            //     debouncedDoSearch(term, resolve, reject);
            // });
        };

        const debouncedDoSearch = _.debounce(async (term, resolve, reject) => {
            try {
                setClaims([]);
                const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
                setClaims(results);
                resolve(results);  // Resolve the promise with the results
            } catch (error) {
                reject(error);  // Reject the promise if there's an error
            }
        }, 800);


        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            authContext.setGroup && authContext.setGroup('messaging-claim-search');
            authContext.cancelGroup && authContext.cancelGroup('messaging-claim-search');
            return await doSearch(term);
        };
        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                messageChangedRef.current = true;
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const composeMessage = async ({
            subject,
            body,
            claimId,
            files,
            links,
            priority,
            dueDate,
        }: IMessage): Promise<void | string> => {
            try {
                // Extract claim IDs from links if available, otherwise use single claimId
                const claimIds = links?.claims?.map(c => c.id).filter(Boolean) || (claimId ? [claimId] : []);
                // Extract category IDs from links if available
                const categoryIds = links?.messageCategories?.map(c => c.id).filter(Boolean);
                // Extract user IDs from links if available
                const assignedUserIds = links?.users?.map(u => u.id).filter(Boolean);
                const messageId = await rootStore.messageStore.composeMessage(
                    rootStore.account,
                    subject,
                    body,
                    claimIds,
                    files,
                    priority,
                    dueDate,
                    categoryIds,
                    assignedUserIds,
                );
                // Mark list as dirty; will refresh on close / message change
                messageChangedRef.current = true;
                toastContext.notify?.('Message sent', 'left');
                return messageId;
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };

        const actualDownloadDocument = async (id: string): Promise<string> => {
            const file = await rootStore.messageStore.download(id);
            return file.url;
        };
        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
            );
            await Promise.all(promises);
            messageChangedRef.current = true;
        };
        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
            );
            await Promise.all(promises);
            messageChangedRef.current = true;
        };
        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const idList = _.concat([], ids);
            const promises = _.map(idList, async (id): Promise<void> => rootStore.messageStore.markAsRead(id));
            await Promise.all(promises);
            messageChangedRef.current = true;

            // If the currently opened message was among the updated IDs, sync its read flag in the detail panel
            const currentId = messageDetail?.message?.id;
            if (currentId && idList.includes(currentId)) {
                const updatedMessage = rootStore.messageStore.findMessage(currentId);
                if (updatedMessage) {
                    const updatedDetail: IMessageDetail = {
                        ...messageDetail,
                        message: updatedMessage,
                    };
                    setMessageDetail(updatedDetail);
                }
            }
        };

        const markAsUnread = async (ids: string | string[]): Promise<void> => {
            const idList = _.concat([], ids);
            const promises = _.map(idList, async (id): Promise<void> => rootStore.messageStore.markAsUnread(id));
            await Promise.all(promises);
            messageChangedRef.current = true;

            // If the currently opened message was among the updated IDs, sync its read flag in the detail panel
            const currentId = messageDetail?.message?.id;
            if (currentId && idList.includes(currentId)) {
                const updatedMessage = rootStore.messageStore.findMessage(currentId);
                if (updatedMessage) {
                    const updatedDetail: IMessageDetail = {
                        ...messageDetail,
                        message: updatedMessage,
                    };
                    setMessageDetail(updatedDetail);
                }
            }
        };
        const messageOpen = async (idOrMessage: string | ProviderMessage): Promise<void> => {
            // Message object always has id, string is validated by caller
            const messageId = typeof idOrMessage === 'string' ? idOrMessage : idOrMessage.id!;
            
            const isCompose = location.pathname.endsWith('/compose');
            const paramsMessageId = params.messageId;
            
            // If we're switching away from a message that has unsynced changes, refresh the list once (in background)
            if (paramsMessageId && paramsMessageId !== messageId && messageChangedRef.current) {
                void refresh();
                messageChangedRef.current = false;
            }

            if (isCompose) {
                navigate(`../${messageId}`, { relative: 'path' });
            } else if (paramsMessageId) {
                navigate(`../${messageId}`, { relative: 'path' });
            } else {
                navigate(messageId);
            }
        };
        const messageClose = async (): Promise<void> => {
            if (messageChangedRef.current) {
                void refresh();
                messageChangedRef.current = false;
            }
            navigate('..', { replace: true, relative: 'path' });
        };

        const showCompose = (): void => {
            const paramsMessageId = params.messageId;
            if (paramsMessageId) {
                navigate('../compose', { relative: 'path' });
            } else {
                navigate('compose');
            }
        };

        const renderMessageList = (): React.ReactElement | undefined => {
            const { doneTotal, doneMessages, inboxTotal, inboxMessages, unreadInboxTotal, messageCategories, providerUsers } = rootStore.messageStore;
            
            const _categories = messageCategories;
            const _users = providerUsers;

            const mapMessages = (messages: typeof storeInboxMessages) => messages.map(m => {
                const categoryNames = _.compact(
                    _.map(m.links?.messageCategories, cat => messageStore.getCategoryName(cat.id || ''))
                );
                const assigneeNames = _.compact(
                    _.map(m.links?.users, user => rootStore.messageStore.getUserName(user.id || ''))
                );
                return {
                    ...m,
                    comments: m.commentCount,
                    documents: m.documentCount,
                    categories: categoryNames,
                    assignees: assigneeNames,
                };
            });

            const customColumns: ITableColumn[] = [
                {
                    id: 'Categories',
                    Header: 'Categories',
                    accessor: 'categories',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
                {
                    id: 'Assignee',
                    Header: 'Assignee',
                    accessor: 'assignees',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
            ];

            return (
                <>
                    <PageLayout>
                        <PageLayoutRow>
                            <PageLayoutHeader>
                                <PageLayoutTitle>Messages</PageLayoutTitle>
                            </PageLayoutHeader>
                        </PageLayoutRow>
                        <PageLayoutRow>
                            <DataTableCard size="large">
                                <S.Wrapper>
                                    <MessageList
                                        doneCount={storeDoneTotal}
                                        doneMessages={mapMessages(storeDoneMessages)}
                                        inboxCount={storeInboxTotal}
                                        inboxMessages={mapMessages(storeInboxMessages)}
                                        unreadCount={storeUnreadInboxTotal}
                                        onFetchData={handleFetchList}
                                        onShowCompose={showCompose}
                                        showFiltering={true}
                                        showNavigation={true}
                                        showSettings={true}
                                        selectedMessageId={messageId}
                                        customColumns={customColumns}
                                    />
                                </S.Wrapper>
                            </DataTableCard>
                        </PageLayoutRow>
                    </PageLayout>
                </>
            );
        };

        const renderComposePanel = (): React.ReactElement | undefined => {
            const state = window.history.state?.usr || {};
            const claimId = state.claimId;
            const subject = state.subject;
            const body = state.body;
            if (!isReadOnly) {
                return (
                    <S.ChatPanelContainer>
                        <ComposePanel claimId={claimId} subject={subject} body={body} hideNote={true} className="fixed" />
                    </S.ChatPanelContainer>
                );
            }
        };

        const paramsMessageId = params.messageId;
        const isComposePath = location.pathname.endsWith('/compose');

        const isMetaLoading = !areCategoriesLoaded || !areUsersLoaded;

        const linkEntityToMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).postProviderMessageLink({
                    id,
                    providerMessageLinkPayload: { entityType, entityIds }
                });
                setClaims([]);
                messageChangedRef.current = true;
            } catch (error) {
                throw error;
            }
        };

        const unlinkEntityFromMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).postProviderMessageUnlink({
                    id,
                    providerMessageLinkPayload: { entityType, entityIds }
                });
                setClaims([]);
                messageChangedRef.current = true;
            } catch (error) {
                throw error;
            }
        };


        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isListLoading || isMetaLoading}
                isDetailLoading={isDetailLoading || isMetaLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetail ? messageDetail.message : undefined}
                comments={messageDetail ? messageDetail.comments : undefined}
                documents={messageDetail ? messageDetail.documents : undefined}
                claims={claims}
                getCategoryName={rootStore.messageStore.getCategoryName}
                getUserName={rootStore.messageStore.getUserName}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onDocumentActualDownload={actualDownloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMarkAsUnread={markAsUnread}
                onMessageClose={messageClose}
                onMessageOpen={messageOpen}
                onLinkEntityToMessage={linkEntityToMessage}
                onUnlinkEntityFromMessage={unlinkEntityFromMessage}>
                {renderMessageList()}
                {isComposePath && renderComposePanel()}
                {paramsMessageId && !isComposePath && (
                    <S.ChatPanelContainer>
                        <ChatPanelImpl onBack={() => navigate('..', { replace: true, relative: 'path' })} />
                    </S.ChatPanelContainer>
                )}
            </MessagingProvider>
        );
    }),
);

const ChatPanelImpl: React.FC<{ 
    onBack: () => void;
}> = ({ onBack }) => {
    return <ChatPanel hideNote={true} className="fixed" onBack={onBack} />;
};
